import { useMemo } from 'react';
import {
  totalLcPricingInr, bcInterestInr, calcBcCfInr,
  fmtL, fmtCr, fmtDateShort, INDEX_BADGE_COLORS,
  useFxRates, useInterestRates, resolveSpot, effectivePaymentDate
} from '../utils/importFinancingUtils';


export default function ImportFinancingDashboard({ lcRecords, bcRecords }) {
  const { rates: fxRates }       = useFxRates();
  const { rates: interestRates } = useInterestRates();

  const ratesReady = Object.keys(fxRates).length > 0;

  const active  = lcRecords.filter(r => r.status !== 'Settled');
  const settled = lcRecords.filter(r => r.status === 'Settled');

  const totalExposure = useMemo(() => {
    if (!ratesReady) return 0;
    return active.reduce((s, r) => s + r.amount_fcy * resolveSpot(r.currency, fxRates, r.spot_rate_inr), 0);
  }, [active, fxRates, ratesReady]);

  const totalLcCost = useMemo(() => {
    if (!ratesReady) return 0;
    return lcRecords.reduce((s, r) => s + totalLcPricingInr(r, fxRates), 0);
  }, [lcRecords, fxRates, ratesReady]);

  const totalBcInterest = useMemo(() => {
    if (!ratesReady) return 0;
    return bcRecords.reduce((s, b) => s + bcInterestInr(b.amount_fcy, b.currency, fxRates, b.spot_rate_inr, b.all_in_rate, b.tenor_days), 0);
  }, [bcRecords, fxRates, ratesReady]);

  const conversions = lcRecords.filter(r =>
    r.status === 'Converted to BC' || bcRecords.some(b => b.lc_id === r.id)
  );

  return (
    <div className="space-y-5">

     {/* Indicative Benchmark Rates */}
<div>
  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
    Indicative Benchmark Rates
  </div>
  <div className="flex gap-3 flex-wrap">
    {[
      { code: 'FED_FUNDS',   label: 'SOFR'  },
      { code: 'ECB_DEPOSIT', label: '€STR'  },
      { code: 'BOE_BASE',    label: 'SONIA' },
    ].map(({ code, label }) => {
      const r = interestRates[code];
      if (!r) return null;
      const badgeColor = INDEX_BADGE_COLORS[label] || 'bg-gray-700 text-gray-300';
      const bpsLabel   = r.overnightSpread != null && r.overnightSpread !== 0
  ? `(${(r.overnightSpread * 100).toFixed(0)}bps vs policy rate)`
  : '';
      return (
        <div key={code} className="flex items-center gap-3 px-4 py-3 bg-gray-900 border border-gray-800 rounded-xl">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>{label}</span>
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-semibold text-white">{r.overnightRate != null ? Number(r.overnightRate).toFixed(2) : '—'}%</span>
              <span className="text-xs text-gray-500">{r.currency}</span>
            </div>
            <div className="text-xs text-gray-500">{r.name} {bpsLabel}</div>
          </div>
        </div>
      );
    })}
  </div>
</div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Import Financing', value: fmtCr(totalExposure), sub: 'LC + BC exposure' },
          { label: 'LC → BC Conversions', value: `${conversions.length} LCs`, sub: 'converted to buyers credit', color: 'text-purple-400' },
          { label: 'Total Financing Cost', value: fmtL(totalLcCost + totalBcInterest), sub: 'LC charges + BC interest' },
          { label: 'Settled This Period', value: `${settled.length} LCs`, sub: 'fully paid out', color: 'text-teal-400' },
        ].map(k => (
          <div key={k.label} className="bg-gray-800 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">{k.label}</div>
            <div className={`text-xl font-semibold ${k.color || 'text-white'}`}>{k.value}</div>
            <div className="text-xs text-gray-500 mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* LC → BC Conversion Tracker */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <span className="text-sm font-medium text-white">LC → Buyers Credit Conversions</span>
          {totalBcInterest > 0 && (
            <span className="text-xs text-gray-400">
              BC savings vs direct loan:&nbsp;
              <span className="text-teal-400 font-medium">{fmtL(totalBcInterest * 0.09)}</span>
            </span>
          )}
        </div>
        {conversions.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500 text-sm">
            No LC conversions yet. Use "Avail BC" on a maturing LC to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-800 text-gray-400 text-left">
                  <th className="px-3 py-2.5 font-medium">LC Ref</th>
                  <th className="px-3 py-2.5 font-medium">Beneficiary</th>
                  <th className="px-3 py-2.5 font-medium">BC Ref</th>
                  <th className="px-3 py-2.5 font-medium">LC Amount</th>
                  <th className="px-3 py-2.5 font-medium">BC Amount</th>
                  <th className="px-3 py-2.5 font-medium">Index</th>
                  <th className="px-3 py-2.5 font-medium">All-in Cost</th>
                  <th className="px-3 py-2.5 font-medium">BC Maturity</th>
                  <th className="px-3 py-2.5 font-medium">Int. Cost</th>
                </tr>
              </thead>
              <tbody>
                {conversions.map(lc => {
                  const bc = bcRecords.find(b => b.lc_id === lc.id);
                  if (!bc) return null;
                  const interest = ratesReady
                    ? bcInterestInr(bc.amount_fcy, bc.currency, fxRates, bc.spot_rate_inr, bc.all_in_rate, bc.tenor_days)
                    : 0;
                  const idxColor = INDEX_BADGE_COLORS[bc.index_type] || INDEX_BADGE_COLORS.Fixed;
                  return (
                    <tr key={lc.id} className="border-t border-gray-800 hover:bg-gray-800/40">
                      <td className="px-3 py-2.5 font-medium text-blue-400">{lc.lc_ref}</td>
                      <td className="px-3 py-2.5 text-gray-300">{lc.beneficiary}</td>
                      <td className="px-3 py-2.5 font-medium text-purple-400">{bc.bc_ref}</td>
                      <td className="px-3 py-2.5 text-gray-300">{lc.currency} {lc.amount_fcy != null ? (lc.amount_fcy/1000).toFixed(0) : '—'}K</td>
<td className="px-3 py-2.5 text-gray-300">{bc.currency} {bc.amount_fcy != null ? (bc.amount_fcy/1000).toFixed(0) : '—'}K</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${idxColor}`}>{bc.index_type}</span>
                      </td>
                     <td className="px-3 py-2.5 font-medium text-white">{bc.all_in_rate != null ? Number(bc.all_in_rate).toFixed(2) : '—'}%</td>
                      <td className="px-3 py-2.5 text-gray-300">{fmtDateShort(bc.repayment_date)}</td>
                      <td className="px-3 py-2.5 text-amber-400">{fmtL(interest)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Maturity Ladder + Cost Breakdown */}
      <div className="grid grid-cols-2 gap-5">
        <MaturityLadder lcRecords={lcRecords} fxRates={fxRates} ratesReady={ratesReady} />
        <CostBreakdown lcCost={totalLcCost} bcInterest={totalBcInterest} />
      </div>
    </div>
  );
}

function MaturityLadder({ lcRecords, fxRates, ratesReady }) {
  const now = new Date();
  const buckets = [
    { label: '0–30 days',   maxDays: 30 },
    { label: '31–60 days',  maxDays: 60 },
    { label: '61–90 days',  maxDays: 90 },
    { label: '91–180 days', maxDays: 180 },
  ];
  const colors = ['#E24B4A', '#BA7517', '#1D9E75', '#378ADD'];

  const data = buckets.map((b, i) => {
    const prev = i === 0 ? 0 : buckets[i - 1].maxDays;
    const lcAmt = lcRecords
      .filter(r => r.status !== 'Settled')
      .filter(r => {
        const effDate = effectivePaymentDate(r);
        const d = Math.ceil((new Date(effDate) - now) / 86400000);
        return d > prev && d <= b.maxDays;
      })
      .reduce((s, r) => s + r.amount_fcy * resolveSpot(r.currency, fxRates, r.spot_rate_inr), 0);
    return { label: b.label, inr: lcAmt, color: colors[i] };
  });
  const max = Math.max(...data.map(d => d.inr), 1);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
      <div className="text-sm font-medium text-white mb-4">Maturity Ladder</div>
      {data.map(d => (
        <div key={d.label} className="mb-3">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-400">{d.label}</span>
            <span className="font-medium text-white">{d.inr > 0 ? fmtCr(d.inr) : '—'}</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.round(d.inr / max * 100)}%`, background: d.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function CostBreakdown({ lcCost, bcInterest }) {
  const lcCharges = lcCost * 0.12;
  const lcComm    = lcCost - lcCharges;

  const items = [
    { label: 'LC Commission',   inr: lcComm,     color: '#378ADD' },
    { label: 'LC Bank Charges', inr: lcCharges,  color: '#7F77DD' },
    { label: 'BC Interest',     inr: bcInterest, color: '#534AB7' },
  ];
  const max = Math.max(...items.map(i => i.inr), 1);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
      <div className="text-sm font-medium text-white mb-4">Cost Breakdown</div>
      {items.map(item => (
        <div key={item.label} className="mb-3">
          <div className="flex justify-between text-xs mb-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm inline-block" style={{ background: item.color }} />
              <span className="text-gray-400">{item.label}</span>
            </span>
            <span className="font-medium text-white">{fmtL(item.inr)}</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.round(item.inr / max * 100)}%`, background: item.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}
